import { useMemo, type ReactNode } from 'react'

import { I18nContext, type I18n } from './i18n-context'
import {
  formatDate,
  formatDateMedium,
  formatDateTime,
  formatList,
  formatNumber,
  formatRelative,
} from './format'
import { DEFAULT_LOCALE, type Locale } from './locale'
import { translate, type TranslateVars } from './translate'

export interface I18nProviderProps {
  /**
   * El idioma de la PERSONA, ya resuelto (`resolveLocale`).
   *
   * Entra como prop y no se lee acá adentro de `GET /auth/me` a propósito:
   * `shared/` no puede importar `features/` sin invertir la dependencia, y
   * además así el portal, los tests y una pantalla suelta pueden fijar el
   * idioma sin montar una sesión entera.
   */
  locale?: Locale
  children: ReactNode
}

export function I18nProvider({ locale = DEFAULT_LOCALE, children }: I18nProviderProps) {
  const value = useMemo<I18n>(
    () => ({
      locale,
      t: (key: string, vars?: TranslateVars) => translate(locale, key, vars),
      formatDate: (value, timeZone) => formatDate(value, locale, timeZone),
      formatDateMedium: (value, timeZone) => formatDateMedium(value, locale, timeZone),
      formatDateTime: (value, timeZone) => formatDateTime(value, locale, timeZone),
      formatNumber: (value, options) => formatNumber(value, locale, options),
      formatRelative: (value, now) => formatRelative(value, locale, now),
      formatList: (items) => formatList(items, locale),
    }),
    [locale],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}
